import React, { useState, useEffect } from 'react'
import { api } from '../../utils/api'
import { itemQuantity } from '../../../../shared/item-count.js'
import { displayDealId } from './testingHelpers'
import DealDetailModal from '../deals/DealDetailModal'

// Design System (matching original warehouse-system)
const theme = {
  colors: {
    charcoal: '#222725',
    sunburst: '#FFD44D',
    greenOnions: '#3BB273',
    imBlue: '#4E8098',
    crimson: '#E5625E',
    porcelain: '#F2EDE4',
    white: '#FFFFFF',
    grey100: '#F8F9F8',
    grey200: '#E7E5E4',
    grey300: '#D6D3D1',
    grey400: '#A8A29E',
    grey500: '#78716C',
    greenLight: '#E8F5EE',
    amberLight: '#FFF9E6',
    redLight: '#FCEEED',
    purple: '#8B5CF6',
    purpleLight: '#F5F3FF'
  },
  fonts: { sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" },
  radius: { sm: '6px', md: '10px', lg: '16px' },
  shadows: { sm: '0 1px 2px rgba(0,0,0,0.05)', md: '0 4px 12px rgba(0,0,0,0.08)' }
}

// Components
const Card = ({ children, style = {}, padding = true, onClick }) => (
  <div onClick={onClick} style={{
    background: theme.colors.white,
    borderRadius: theme.radius.lg,
    boxShadow: theme.shadows.sm,
    border: `1px solid ${theme.colors.grey200}`,
    padding: padding ? '24px' : 0,
    ...style
  }}>
    {children}
  </div>
)

const Badge = ({ children, color = 'grey' }) => {
  const colors = {
    grey: { bg: theme.colors.grey200, text: theme.colors.grey500 },
    green: { bg: theme.colors.greenLight, text: theme.colors.greenOnions },
    amber: { bg: theme.colors.amberLight, text: '#B8860B' },
    red: { bg: theme.colors.redLight, text: theme.colors.crimson },
    purple: { bg: theme.colors.purpleLight, text: theme.colors.purple }
  }
  const c = colors[color] || colors.grey
  return (
    <span style={{
      display: 'inline-flex',
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 600,
      background: c.bg,
      color: c.text
    }}>
      {children}
    </span>
  )
}

// The receiver matches a box by what is inside it, so panel rows name the items.
const PanelItems = ({ items }) => (
  <>
    {items.slice(0, 3).map((item, i) => (
      <div key={i} style={{ fontSize: '13px', overflowWrap: 'anywhere' }}>
        {item.name}{itemQuantity(item) > 1 ? ` ×${itemQuantity(item)}` : ''}
      </div>
    ))}
    {items.length > 3 && (
      <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>+{items.length - 3} more</div>
    )}
  </>
)

const panelRowStyle = {
  display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer', boxSizing: 'border-box',
  padding: '10px 12px', marginBottom: '8px', fontFamily: 'inherit', color: 'inherit', textDecoration: 'none',
  background: theme.colors.grey100, border: `1px solid ${theme.colors.grey200}`, borderRadius: theme.radius.md
}

const panelGroupLabelStyle = {
  fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: theme.colors.grey500, margin: '12px 0 8px'
}

// 'quote_requested' -> 'Quote Requested'. A quote with everything arrived says so,
// because its stage ("In Warehouse") would read as something still to do.
const relatedStageLabel = (deal) => {
  if (deal.finished && !['closed_won', 'closed_lost', 'archived'].includes(deal.stage)) return 'All arrived'
  if (deal.stage === 'closed_won') return 'Closed Traded'
  return String(deal.stage || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

const ACTION = {
  receiving: { check: 'canReceive', label: 'Receive', group: 'Ready to receive' },
  testing: { check: 'canTest', label: 'Test', group: 'Ready to test' }
}

// One deal. The row opens the deal in place; an actionable deal also gets the screen's button.
const DealRow = ({ deal, action, onOpen, onAct }) => {
  const canAct = deal[action.check]?.ok
  // presentRelated marks a received in-warehouse deal finished. On Testing that deal is
  // the one to test, so only the panel group decides "All arrived".
  const label = relatedStageLabel({ ...deal, finished: deal.group === 'finished' })
  return (
    <div style={canAct ? { ...panelRowStyle, cursor: 'default', background: theme.colors.greenLight, borderColor: theme.colors.greenOnions } : { ...panelRowStyle, cursor: 'default' }}>
      <button
        onClick={() => onOpen(deal)}
        aria-label={`Open ${displayDealId(deal)}`}
        style={{ all: 'unset', display: 'block', width: '100%', cursor: 'pointer' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
          <span style={{ fontWeight: 600, fontSize: '14px' }}>{displayDealId(deal)}</span>
          <Badge color={canAct ? 'green' : deal.group === 'finished' ? 'grey' : 'amber'}>{label}</Badge>
        </div>
        <PanelItems items={deal.items || []} />
      </button>
      {canAct && (
        <button
          onClick={() => onAct(deal)}
          aria-label={`${action.label} ${displayDealId(deal)}`}
          style={{ marginTop: '8px', padding: '6px 12px', fontSize: '13px', fontWeight: 600, fontFamily: 'inherit', border: 'none', borderRadius: theme.radius.md, background: theme.colors.sunburst, color: theme.colors.charcoal, cursor: 'pointer' }}
        >
          {action.label}
        </button>
      )}
    </div>
  )
}

/**
 * The same customer's other deals, for the deal a warehouse screen found.
 * Actionable deals (any age) first with a Receive or Test button, then open deals
 * from the last 30 days, then finished deals collapsed. A row click opens the
 * deal in place, so the work on screen is kept.
 */
export default function CustomerDealsPanel({ dealId, screen, onAct }) {
  const [deals, setDeals] = useState([])
  const [failed, setFailed] = useState(false)
  const [modalDeal, setModalDeal] = useState(null)
  const action = ACTION[screen]

  useEffect(() => {
    let current = true
    setDeals([])
    setFailed(false)
    api.get(`/api/warehouse/${screen}/customer-deals?dealId=${encodeURIComponent(dealId)}`)
      // A slow answer for a deal the screen has already left must not land.
      .then(response => { if (current) setDeals(response.deals || []) })
      .catch(() => { if (current) setFailed(true) })
    return () => { current = false }
  }, [dealId, screen])

  // Same shape IssueTrades passes to the modal.
  const openModal = (deal) => setModalDeal({
    id: deal.hubspotDealId || deal.id, quote_id: deal.id, reference_code: deal.reference, dealname: deal.reference
  })

  const inGroup = (group) => deals.filter(d => d.group === group)
  const actionable = inGroup('actionable')
  const open = inGroup('open')
  const finished = inGroup('finished')
  if (!failed && actionable.length + open.length + finished.length === 0) return null

  const row = (deal) => <DealRow key={deal.id} deal={deal} action={action} onOpen={openModal} onAct={onAct} />

  return (
    <Card style={{ padding: '16px' }}>
      <div style={{ fontSize: '14px', fontWeight: 700 }}>Other deals from this customer</div>
      {failed && (
        <div style={{ fontSize: '13px', color: theme.colors.crimson, marginTop: '12px' }}>
          Could not load this customer's other deals. Search again to retry.
        </div>
      )}
      {actionable.length > 0 && <div style={panelGroupLabelStyle}>{action.group}</div>}
      {actionable.map(row)}
      {open.length > 0 && <div style={panelGroupLabelStyle}>Other open deals</div>}
      {open.map(row)}
      {finished.length > 0 && (
        <details style={{ borderTop: `1px solid ${theme.colors.grey200}`, paddingTop: '12px', marginTop: '12px' }}>
          <summary style={{ cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: theme.colors.grey500, marginBottom: '8px' }}>
            {finished.length} finished deal{finished.length !== 1 ? 's' : ''}
          </summary>
          {finished.map(row)}
        </details>
      )}
      {modalDeal && <DealDetailModal deal={modalDeal} onClose={() => setModalDeal(null)} />}
    </Card>
  )
}
